import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, PartyPopper, RefreshCw } from "lucide-react";

import type { Movimiento } from "../api/tipos.ts";
import { usarDecidir, usarDecidirLote, usarLibro, type Decision } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { pesos, plural } from "../formato.ts";
import { ErrorConsulta, EsqueletoLista } from "../componentes/Errores.tsx";
import { FilaPago } from "../componentes/FilaPago.tsx";
import { AvisoHecho } from "../componentes/AvisoHecho.tsx";
import { cargaDecision, mensajeDeDecision } from "../componentes/decision-modelo.ts";
import { GRUPOS, enOrdenDeBandeja, grupoDe, viaDeFactura, type Grupo } from "../componentes/atajo-de-pago.ts";
import { cargaDeLote, escribiendo, seleccionables, teclaDe } from "../componentes/mesa-modelo.ts";
import { Aviso, Boton, Dialogo, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";

/**
 * La bandeja es el producto.
 *
 * Todo lo demás del panel se puede mirar de vez en cuando; esto es lo que se
 * abre cada día. Los casos más delicados —los que ya se habían decidido y
 * cambiaron— van arriba del todo.
 *
 * Una fila por pago y no una tarjeta (30-sep-2026): con tarjetas de 540 px, los
 * 168 pendientes de Lorus eran ochenta pantallas. La tarjeta sigue ahí, dentro
 * de cada fila, para los casos que hay que pensar.
 */

export function Bandeja() {
  const { data: libro, isPending, error, refetch, isFetching } = usarLibro();
  const decidir = usarDecidir();
  const { puedo } = usarSesion();
  const [filtro, setFiltro] = useState<Grupo | "todo">("todo");
  // Lo último decidido, para el mensaje y para poder deshacerlo (SPEC-029 §7).
  const [ultima, setUltima] = useState<{ texto: string; clave?: string; id: number } | null>(null);

  // El aviso de Telegram enlaza a `/bandeja?pago=<clave>` (SPEC-029 §10): esa
  // fila se abre y se trae a la vista. Si el filtro la escondería, se quita.
  const [params] = useSearchParams();
  const pagoPedido = params.get("pago") ?? "";
  useEffect(() => {
    if (pagoPedido) setFiltro("todo");
  }, [pagoPedido]);

  const pendientes = useMemo(() => {
    const lista = enOrdenDeBandeja(libro?.pendientes ?? []);
    return filtro === "todo" ? lista : lista.filter((m) => grupoDe(m) === filtro);
  }, [libro?.pendientes, filtro]);

  const grupos = useMemo(() => contarPorGrupo(libro?.pendientes ?? []), [libro?.pendientes]);

  // ── la mesa de conciliación (SPEC-029 §14) ──
  const lote = usarDecidirLote();
  const puedeDecidir = puedo(PERMISO.DECIDIR);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [confirmandoLote, setConfirmandoLote] = useState(false);
  const enLote = useMemo(() => new Set(seleccionables(pendientes).map((m) => m.clave)), [pendientes]);
  // Lo marcado que sigue pendiente y seleccionable: lo demás se suelta solo.
  const marcadosVivos = pendientes.filter((m) => marcados.has(m.clave) && enLote.has(m.clave));
  const marcar = (clave: string, si: boolean) =>
    setMarcados((prev) => {
      const sig = new Set(prev);
      if (si) sig.add(clave);
      else sig.delete(clave);
      return sig;
    });

  function confirmarLote() {
    const cargas = marcadosVivos.map(cargaDeLote).filter((d): d is Decision => d !== null);
    lote.mutate(cargas, {
      onSuccess: (r) => {
        setConfirmandoLote(false);
        setMarcados(new Set());
        const fallidas = r.resultados.filter((x) => !x.ok);
        setUltima({
          texto:
            `Confirmados ${r.guardadas} ${plural(r.guardadas, "pago", "pagos")}.` +
            (fallidas.length ? ` ${fallidas.length} no se pudieron: ${fallidas.map((x) => x.error).join("; ")}` : ""),
          id: Date.now(),
        });
      },
    });
  }

  // El teclado: j/k para moverse, Enter para abrir, s para el sí, n para «no
  // es de este cliente», x para marcar. Solo cuando no se está escribiendo.
  const [activa, setActiva] = useState(-1);
  const [comando, setComando] = useState<{ clave: string; accion: "abrir"; n: number } | null>(null);
  useEffect(() => {
    if (!puedeDecidir) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (escribiendo(document.activeElement) || document.querySelector("dialog[open]")) return;
      const t = teclaDe(e);
      if (!t || !pendientes.length) return;
      e.preventDefault();
      if (t === "siguiente") return setActiva((i) => Math.min(i + 1, pendientes.length - 1));
      if (t === "anterior") return setActiva((i) => Math.max(i - 1, 0));
      const m = pendientes[activa];
      if (!m) return;
      if (t === "abrir") return setComando({ clave: m.clave, accion: "abrir", n: Date.now() });
      if (t === "marcar") return enLote.has(m.clave) && marcar(m.clave, !marcados.has(m.clave));
      if (t === "si") {
        const d = cargaDeLote(m);
        return d ? alDecidir(d, m) : setComando({ clave: m.clave, accion: "abrir", n: Date.now() });
      }
      if (t === "no") {
        const via = viaDeFactura(m);
        if (via.tipo === "revisar") return;
        const accion = via.tipo === "identificar" && !via.conflicto ? "DESCARTAR" : "RECHAZAR";
        return alDecidir(cargaDecision({ clave: m.clave, accion }), m);
      }
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  });

  function alDecidir(d: Decision, m: Movimiento) {
    decidir.mutate(d, {
      onSuccess: () =>
        setUltima({
          texto: mensajeDeDecision(d, m.contraparte),
          // Un ANULAR no se ofrece deshacer: deshacer lo deshecho es decidir otra vez.
          clave: d.accion === "ANULAR" ? undefined : d.clave,
          id: Date.now(),
        }),
    });
  }

  function deshacer(clave: string) {
    decidir.mutate(cargaDecision({ clave, accion: "ANULAR" }), {
      onSuccess: () =>
        setUltima({ texto: mensajeDeDecision({ clave, accion: "ANULAR" }, ""), id: Date.now() }),
    });
  }

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  return (
    <Seccion
      titulo="Esperan tu decisión"
      descripcion={
        isPending
          ? "Cargando tus pagos…"
          : `${pendientes.length} ${plural(pendientes.length, "caso", "casos")} donde el sistema prefirió preguntar antes que adivinar.`
      }
      accion={
        <Boton
          variante="fantasma"
          tamano="sm"
          icono={RefreshCw}
          cargando={isFetching && !isPending}
          onClick={() => void refetch()}
        >
          Actualizar
        </Boton>
      }
    >
      {!puedo(PERMISO.DECIDIR) && (
        <Aviso tono="dato" titulo="Tu rol es de solo lectura">
          Puedes ver qué está pendiente, pero decidir le corresponde a otro rol.
        </Aviso>
      )}

      {ultima && (
        <AvisoHecho
          key={ultima.id}
          texto={ultima.texto}
          alDeshacer={ultima.clave ? () => deshacer(ultima.clave!) : undefined}
          deshaciendo={decidir.isPending && decidir.variables?.accion === "ANULAR"}
        />
      )}

      {decidir.error && <ErrorConsulta error={decidir.error} />}

      {grupos.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="size-4 text-tenue-2" aria-hidden />
          <Chip activo={filtro === "todo"} alPulsar={() => setFiltro("todo")}>
            Todos ({libro?.pendientes.length ?? 0})
          </Chip>
          {grupos.map(({ grupo, texto, n }) => (
            <Chip key={grupo} activo={filtro === grupo} alPulsar={() => setFiltro(grupo)}>
              {texto} ({n})
            </Chip>
          ))}
        </div>
      )}

      {/* Esto salía repetido dentro de cada tarjeta. Con dos casos era ruido;
          con veinte, media pantalla de texto idéntico. Dicho una vez, arriba,
          se lee igual de bien y la lista cabe. */}
      {!isPending && pendientes.length > 0 && (
        <details className="group max-w-prose text-sm text-tenue">
          <summary className="cursor-pointer text-xs font-medium text-tenue hover:text-tinta">
            Cómo funciona
          </summary>
          <div className="mt-2 space-y-2 leading-relaxed">
            <p>
              Cuando entra plata que no esperabas, dinos de quién es y el siguiente pago suyo se
              resuelve solo. Quede lo que quede decidido, se guarda con tu nombre y la hora:
              corregir es agregar otra decisión, nunca borrar la anterior.
            </p>
            <p>
              Los iconos de cada fila son, en orden, nombre, monto, fecha y factura: ✓ coincide, ⚠
              no cuadra del todo, ? no se pudo comprobar. Una factura punteada la sugiere tu
              cartera; se liga al confirmar. Si hay una factura que ligar, el «sí» la lleva puesta.
            </p>
            <p>
              Con el teclado: <kbd>j</kbd> y <kbd>k</kbd> para moverte, <kbd>Enter</kbd> para abrir,{" "}
              <kbd>s</kbd> para el sí de la fila, <kbd>n</kbd> para «no es de este cliente» y{" "}
              <kbd>x</kbd> para marcarla en el lote.
            </p>
          </div>
        </details>
      )}

      {isPending ? (
        <EsqueletoLista />
      ) : pendientes.length === 0 ? (
        <Tarjeta>
          <Vacio icono={PartyPopper} titulo="No hay nada que decidir">
            {libro?.pendientes.length
              ? "Ningún caso coincide con ese filtro."
              : "Todos los pagos del periodo están resueltos. Cuando entre uno que 4drando no pueda explicar solo, aparecerá aquí."}
          </Vacio>
        </Tarjeta>
      ) : (
        <>
          {puedeDecidir && enLote.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-borde bg-superficie-2/60 px-4 py-2 text-sm">
              <label className="flex items-center gap-2 text-tenue">
                <input
                  type="checkbox"
                  checked={marcadosVivos.length === enLote.size}
                  onChange={(e) => setMarcados(e.target.checked ? new Set(enLote) : new Set())}
                  className="size-4 accent-[var(--color-marca)]"
                />
                Marcar los {enLote.size} donde todo coincide
              </label>
              {marcadosVivos.length > 0 && (
                <Boton tamano="sm" variante="primario" onClick={() => setConfirmandoLote(true)}>
                  Confirmar {marcadosVivos.length}
                </Boton>
              )}
            </div>
          )}
          <ul className="tarjeta @container overflow-visible">
            {pendientes.map((m, i) => (
              <FilaPago
                key={m.clave}
                movimiento={m}
                cartera={libro?.facturas ?? []}
                puedeDecidir={puedeDecidir}
                decidiendo={decidir.isPending && decidir.variables?.clave === m.clave}
                alDecidir={(d) => alDecidir(d, m)}
                pedido={m.clave === pagoPedido}
                activa={i === activa}
                seleccionable={enLote.has(m.clave)}
                seleccionado={marcados.has(m.clave)}
                alSeleccionar={puedeDecidir && enLote.size > 0 ? (si) => marcar(m.clave, si) : undefined}
                comando={comando?.clave === m.clave ? comando : undefined}
              />
            ))}
          </ul>
          <Dialogo
            abierto={confirmandoLote}
            alCerrar={() => setConfirmandoLote(false)}
            titulo={`Confirmar ${marcadosVivos.length} ${plural(marcadosVivos.length, "pago", "pagos")}`}
            descripcion="En todos coincide lo que se pudo comprobar. Cada uno se confirma como dice su botón."
            pie={
              <>
                <Boton variante="fantasma" onClick={() => setConfirmandoLote(false)}>
                  Cancelar
                </Boton>
                <Boton variante="primario" cargando={lote.isPending} onClick={confirmarLote}>
                  Confirmar {marcadosVivos.length}
                </Boton>
              </>
            }
          >
            <ul className="max-h-80 space-y-1 overflow-auto text-sm">
              {marcadosVivos.map((m) => (
                <li key={m.clave} className="flex gap-3">
                  <span className="cifra w-24 shrink-0 font-medium">{pesos(m.montoCentavos)}</span>
                  <span className="min-w-0 flex-1 truncate">{m.contraparte}</span>
                  <span className="truncate text-tenue">{m.cliente}</span>
                  {(cargaDeLote(m)?.facturas ?? (m.factura ? [m.factura] : [])).length > 0 && (
                    <span className="cifra shrink-0 text-xs text-tenue">
                      {(cargaDeLote(m)?.facturas ?? [m.factura]).join(", ")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Dialogo>
        </>
      )}
    </Seccion>
  );
}

/** Los grupos que tienen algo, en el orden de `GRUPOS`: lo que cambió, primero. */
function contarPorGrupo(pendientes: Movimiento[]) {
  const cuenta = new Map<Grupo, number>();
  for (const m of pendientes) cuenta.set(grupoDe(m), (cuenta.get(grupoDe(m)) ?? 0) + 1);
  return GRUPOS.filter((g) => cuenta.has(g.grupo)).map((g) => ({ ...g, n: cuenta.get(g.grupo) ?? 0 }));
}

function Chip({
  activo,
  alPulsar,
  children,
}: {
  activo: boolean;
  alPulsar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={alPulsar}
      className={cx(
        "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
        activo
          ? "border-marca bg-marca-suave text-marca-tinta"
          : "border-borde bg-superficie text-tenue hover:border-borde-fuerte hover:text-tinta",
      )}
    >
      {children}
    </button>
  );
}
